// ═══════════════════════════════════════════════════════════════════════════════
// Admin — system settings (limits_by_level, trial window, retention,
// subscription upgrade copy)
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_MEMBERSHIP_PRICING_CATALOG,
  DEFAULT_SUBSCRIPTION_UPGRADE_COPY,
  DEFAULT_SUBSCRIPTION_PROMOTIONS,
  isMembershipPricingLockedKey,
  normalizeMembershipPricingCatalogSetting,
  normalizeSubscriptionPromotionsSetting,
  type LimitedTimePromoOffer,
  SUBSCRIPTION_UPGRADE_COPY_AMOUNT_PLACEHOLDER,
  SUBSCRIPTION_UPGRADE_COPY_DATE_PLACEHOLDER,
  SUBSCRIPTION_UPGRADE_COPY_DUE_AMOUNT_PLACEHOLDER,
  SUBSCRIPTION_UPGRADE_COPY_END_PLACEHOLDER,
  SUBSCRIPTION_UPGRADE_COPY_ENDS_AT_PLACEHOLDER,
  SUBSCRIPTION_UPGRADE_COPY_LIST_PRICE_PLACEHOLDER,
  SUBSCRIPTION_UPGRADE_COPY_PERIOD_PLACEHOLDER,
  SUBSCRIPTION_UPGRADE_COPY_START_PLACEHOLDER,
  TASK_KIND,
  type EmployerLevelLimitsConfig,
  type LimitsByLevelAdminMap,
  type MembershipPricingCatalogItem,
  type MembershipPricingCatalogSetting,
  type MembershipPricingLocale,
  type PhotoRetentionDaysByLevel,
  type PinMessageLimits,
  type PinMessageLimitsByLevel,
  type SubscriptionUpgradeCopyLocale,
  type SubscriptionUpgradeCopySetting,
  type SystemSettings,
  type TaskKind,
  ADMIN_ROLE,
} from '@fdh/shared';
import { adminApi } from '../../lib/adminApi';
import { hktWallToUtcWall, utcWallToHktWall } from '../../lib/adminHktDatetime';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { AdminMaintenanceForm } from './AdminMaintenanceForm';

const LEVEL_KEYS = ['1', '2', '3'] as const;
const LEVEL_LABELS: Record<(typeof LEVEL_KEYS)[number], string> = {
  '1': 'Level 1 (Trial)',
  '2': 'Level 2 (Free)',
  '3': 'Level 3 (Paid)',
};

const TASK_KIND_OPTIONS: { value: TaskKind; label: string }[] = [
  { value: TASK_KIND.STANDARD, label: 'standard' },
  { value: TASK_KIND.INVENTORY, label: 'inventory' },
  { value: TASK_KIND.PICKUP_DROPOFF, label: 'pickup_dropoff' },
  { value: TASK_KIND.MEDICATION_REMINDER, label: 'medication_reminder' },
];

const num = (v: string): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const cloneUpgradeCopy = (
  value: SubscriptionUpgradeCopySetting | null | undefined,
): SubscriptionUpgradeCopySetting => {
  const source = value ?? DEFAULT_SUBSCRIPTION_UPGRADE_COPY;
  return {
    defaultLocale: source.defaultLocale,
    locales: Object.fromEntries(
      Object.entries(source.locales).map(([locale, row]) => [locale, { ...row }]),
    ),
  };
};

type UpgradeCopyFieldDef = {
  key: keyof SubscriptionUpgradeCopyLocale;
  label: string;
  multiline?: boolean;
  hint?: string;
};

const UPGRADE_COPY_FIELD_GROUPS: { heading: string; fields: UpgradeCopyFieldDef[] }[] = [
  {
    heading: 'Normal (charge today, no promo)',
    fields: [
      { key: 'payTodayTitle', label: 'Title', multiline: true },
      {
        key: 'payTodayAmount',
        label: 'Amount line',
        hint: `${SUBSCRIPTION_UPGRADE_COPY_AMOUNT_PLACEHOLDER}, ${SUBSCRIPTION_UPGRADE_COPY_PERIOD_PLACEHOLDER} (每月/每年)`,
      },
    ],
  },
  {
    heading: 'Promo pricing (charge today with discount)',
    fields: [
      {
        key: 'promoFirstMonthPrice',
        label: 'First-month price line',
        hint: `${SUBSCRIPTION_UPGRADE_COPY_DUE_AMOUNT_PLACEHOLDER}, ${SUBSCRIPTION_UPGRADE_COPY_LIST_PRICE_PLACEHOLDER}, ${SUBSCRIPTION_UPGRADE_COPY_PERIOD_PLACEHOLDER}`,
      },
      {
        key: 'promoLimitedTimePrice',
        label: 'Limited-time price line',
        hint: `${SUBSCRIPTION_UPGRADE_COPY_DUE_AMOUNT_PLACEHOLDER}, ${SUBSCRIPTION_UPGRADE_COPY_LIST_PRICE_PLACEHOLDER}, ${SUBSCRIPTION_UPGRADE_COPY_ENDS_AT_PLACEHOLDER}, ${SUBSCRIPTION_UPGRADE_COPY_PERIOD_PLACEHOLDER}`,
      },
      {
        key: 'promoFirstMonthBadge',
        label: 'First-month badge',
        hint: SUBSCRIPTION_UPGRADE_COPY_AMOUNT_PLACEHOLDER,
      },
      {
        key: 'promoLimitedTimeBadge',
        label: 'Limited-time badge',
        hint: `${SUBSCRIPTION_UPGRADE_COPY_AMOUNT_PLACEHOLDER}, ${SUBSCRIPTION_UPGRADE_COPY_ENDS_AT_PLACEHOLDER}`,
      },
    ],
  },
  {
    heading: 'Deferred first payment (invitation premium trial / no charge today)',
    fields: [
      { key: 'deferredTitle', label: 'Title', multiline: true },
      { key: 'deferredNoChargeToday', label: 'No charge today line' },
      {
        key: 'deferredFirstChargeOn',
        label: 'First charge on line',
        hint: SUBSCRIPTION_UPGRADE_COPY_DATE_PLACEHOLDER,
      },
      {
        key: 'deferredCoversPeriod',
        label: 'First paid period line',
        hint: `${SUBSCRIPTION_UPGRADE_COPY_START_PLACEHOLDER}, ${SUBSCRIPTION_UPGRADE_COPY_END_PLACEHOLDER}`,
      },
      {
        key: 'deferredInvitationTrialNote',
        label: 'Invitation trial note',
        hint: SUBSCRIPTION_UPGRADE_COPY_DATE_PLACEHOLDER,
      },
    ],
  },
];

const cloneLimitsByLevel = (map: LimitsByLevelAdminMap): LimitsByLevelAdminMap => ({
  '1': { ...map['1'], allowedTaskKinds: [...map['1'].allowedTaskKinds] },
  '2': { ...map['2'], allowedTaskKinds: [...map['2'].allowedTaskKinds] },
  '3': { ...map['3'], allowedTaskKinds: [...map['3'].allowedTaskKinds] },
});

const cloneMembershipPricingCatalog = (
  value: MembershipPricingCatalogSetting | null | undefined,
): MembershipPricingCatalogSetting => {
  const normalized = normalizeMembershipPricingCatalogSetting(
    value ?? DEFAULT_MEMBERSHIP_PRICING_CATALOG,
  );
  return {
    items: normalized.items.map((row) => ({
      ...row,
      label: { ...row.label },
      remark: { ...row.remark },
    })),
  };
};

/** Normalize API settings and convert UTC wall-clock fields to HKT for admin UI. */
const withNormalizedSettings = (data: SystemSettings): SystemSettings => {
  const promotions = normalizeSubscriptionPromotionsSetting(
    data.subscriptionPromotions ?? DEFAULT_SUBSCRIPTION_PROMOTIONS,
  );
  return {
    ...data,
    playStoreShowOfficialWebsiteUrl: data.playStoreShowOfficialWebsiteUrl === true,
    featureLaunchAt: utcWallToHktWall(data.featureLaunchAt),
    subscriptionUpgradeCopy: cloneUpgradeCopy(data.subscriptionUpgradeCopy),
    subscriptionPromotions: {
      newUserFirstMonth: { ...promotions.newUserFirstMonth },
      limitedTimeOffers: promotions.limitedTimeOffers.map((offer) => ({
        ...offer,
        startsAt: utcWallToHktWall(offer.startsAt),
        endsAt: utcWallToHktWall(offer.endsAt),
      })),
    },
    membershipPricingCatalog: cloneMembershipPricingCatalog(data.membershipPricingCatalog),
    limitsByLevel: cloneLimitsByLevel(data.limitsByLevel),
    photoRetentionDaysByLevel: { ...data.photoRetentionDaysByLevel },
    pinMessageLimitsByLevel: {
      '1': { ...data.pinMessageLimitsByLevel['1'] },
      '2': { ...data.pinMessageLimitsByLevel['2'] },
      '3': { ...data.pinMessageLimitsByLevel['3'] },
    },
  };
};

/** Convert HKT UI fields back to UTC wall-clock for the settings API. */
const toApiSettingsPayload = (draft: SystemSettings): SystemSettings => ({
  ...draft,
  featureLaunchAt: hktWallToUtcWall(draft.featureLaunchAt),
  subscriptionPromotions: {
    ...draft.subscriptionPromotions,
    limitedTimeOffers: draft.subscriptionPromotions.limitedTimeOffers.map((offer) => ({
      ...offer,
      startsAt: hktWallToUtcWall(offer.startsAt),
      endsAt: hktWallToUtcWall(offer.endsAt),
    })),
  },
});

const createEmptyLimitedTimeOffer = (): LimitedTimePromoOffer => ({
  id:
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `offer_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  enabled: false,
  billingInterval: 'month',
  amountOff: 0,
  stripeCouponId: '',
  startsAt: '2026-01-01 00:00',
  endsAt: '2026-01-01 00:00',
  adminNote: '',
});

const CATALOG_LOCALES: MembershipPricingLocale[] = ['en', 'zh-Hant', 'zh-Hans'];

const updateCatalogItem = (
  catalog: MembershipPricingCatalogSetting,
  key: MembershipPricingCatalogItem['key'],
  patch: Partial<MembershipPricingCatalogItem>,
): MembershipPricingCatalogSetting =>
  normalizeMembershipPricingCatalogSetting({
    items: catalog.items.map((row) => (row.key === key ? { ...row, ...patch } : row)),
  });

const moveCatalogItem = (
  catalog: MembershipPricingCatalogSetting,
  key: MembershipPricingCatalogItem['key'],
  direction: -1 | 1,
): MembershipPricingCatalogSetting => {
  if (isMembershipPricingLockedKey(key)) {
    return catalog;
  }
  const unlocked = catalog.items.filter((row) => !isMembershipPricingLockedKey(row.key));
  const locked = catalog.items.filter((row) => isMembershipPricingLockedKey(row.key));
  const index = unlocked.findIndex((row) => row.key === key);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= unlocked.length) {
    return catalog;
  }
  const nextUnlocked = [...unlocked];
  const [moved] = nextUnlocked.splice(index, 1);
  if (!moved) {
    return catalog;
  }
  nextUnlocked.splice(target, 0, moved);
  return normalizeMembershipPricingCatalogSetting({
    items: [
      ...locked,
      ...nextUnlocked.map((row, order) => ({ ...row, sortOrder: order + 2 })),
    ],
  });
};

const inputClass =
  'w-full max-w-xs rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white';

type SettingsTab = 'membership' | 'register' | 'subscription' | 'marketing' | 'maintenance';

type NullableNumberFieldProps = {
  id: string;
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
  unlimitedLabel?: string;
};

const NullableNumberField = ({
  id,
  label,
  value,
  onChange,
  unlimitedLabel = 'Unlimited (null)',
}: NullableNumberFieldProps) => {
  const isUnlimited = value === null;
  return (
    <div>
      <label className="mb-1 block text-xs text-slate-400" htmlFor={id}>
        {label}
      </label>
      <div className="flex flex-wrap items-center gap-3">
        <input
          id={id}
          type="number"
          min={0}
          disabled={isUnlimited}
          value={isUnlimited ? '' : value}
          onChange={(ev) => onChange(num(ev.target.value))}
          className={inputClass}
          aria-label={label}
        />
        <label className="flex items-center gap-2 text-xs text-slate-300">
          <input
            type="checkbox"
            checked={isUnlimited}
            onChange={(ev) => onChange(ev.target.checked ? null : 0)}
          />
          {unlimitedLabel}
        </label>
      </div>
    </div>
  );
};

export const AdminSettingsPage = () => {
  const { t } = useLanguage();
  const { admin } = useAdminAuth();
  const isSuperAdmin = admin?.role === ADMIN_ROLE.SUPER_ADMIN;
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [draft, setDraft] = useState<SystemSettings | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [newLocaleCode, setNewLocaleCode] = useState('');
  const [tab, setTab] = useState<SettingsTab>('membership');

  const tabClass = (value: SettingsTab): string =>
    [
      'rounded-md px-4 py-2 text-sm font-medium transition-colors',
      tab === value ? 'bg-amber-500 text-slate-950' : 'bg-slate-800 text-slate-300 hover:bg-slate-700',
    ].join(' ');

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await adminApi.get<SystemSettings>('/admin/settings');
      if (res.success && res.data) {
        const normalized = withNormalizedSettings(res.data);
        setSettings(normalized);
        setDraft(normalized);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('auth.errorGeneric'));
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = useCallback(async () => {
    if (!draft) return;
    setSaving(true);
    setError('');
    try {
      const res = await adminApi.put<SystemSettings>(
        '/admin/settings',
        toApiSettingsPayload(draft),
      );
      if (res.success && res.data) {
        const normalized = withNormalizedSettings(res.data);
        setSettings(normalized);
        setDraft(normalized);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('auth.errorGeneric'));
    } finally {
      setSaving(false);
    }
  }, [draft, t]);

  const localeCodes = useMemo(
    () =>
      draft
        ? Object.keys(draft.subscriptionUpgradeCopy.locales).sort((a, b) => a.localeCompare(b))
        : [],
    [draft],
  );

  const handleAddLocale = useCallback(() => {
    const code = newLocaleCode.trim();
    if (!code || !draft) {
      return;
    }
    if (draft.subscriptionUpgradeCopy.locales[code]) {
      setError(`Locale "${code}" already exists`);
      return;
    }
    const fallback =
      draft.subscriptionUpgradeCopy.locales[draft.subscriptionUpgradeCopy.defaultLocale] ??
      DEFAULT_SUBSCRIPTION_UPGRADE_COPY.locales.en;
    setDraft({
      ...draft,
      subscriptionUpgradeCopy: {
        ...draft.subscriptionUpgradeCopy,
        locales: {
          ...draft.subscriptionUpgradeCopy.locales,
          [code]: { ...fallback },
        },
      },
    });
    setNewLocaleCode('');
    setError('');
  }, [draft, newLocaleCode]);

  const handleRemoveLocale = useCallback(
    (code: string) => {
      if (!draft) {
        return;
      }
      const nextLocales = { ...draft.subscriptionUpgradeCopy.locales };
      delete nextLocales[code];
      if (Object.keys(nextLocales).length === 0) {
        setError('At least one locale is required');
        return;
      }
      const nextDefault =
        draft.subscriptionUpgradeCopy.defaultLocale === code
          ? Object.keys(nextLocales)[0]!
          : draft.subscriptionUpgradeCopy.defaultLocale;
      setDraft({
        ...draft,
        subscriptionUpgradeCopy: {
          defaultLocale: nextDefault,
          locales: nextLocales,
        },
      });
      setError('');
    },
    [draft],
  );

  const handleLevelFieldChange = useCallback(
    <K extends keyof EmployerLevelLimitsConfig>(
      levelKey: (typeof LEVEL_KEYS)[number],
      field: K,
      value: EmployerLevelLimitsConfig[K],
    ) => {
      setDraft((prev) => {
        if (!prev) {
          return prev;
        }
        return {
          ...prev,
          limitsByLevel: {
            ...prev.limitsByLevel,
            [levelKey]: {
              ...prev.limitsByLevel[levelKey],
              [field]: value,
            },
          },
        };
      });
    },
    [],
  );

  const handleToggleTaskKind = useCallback(
    (levelKey: (typeof LEVEL_KEYS)[number], kind: TaskKind, checked: boolean) => {
      setDraft((prev) => {
        if (!prev) {
          return prev;
        }
        const current = prev.limitsByLevel[levelKey].allowedTaskKinds;
        const nextKinds = checked
          ? current.includes(kind)
            ? current
            : [...current, kind]
          : current.filter((item) => item !== kind);
        if (nextKinds.length === 0) {
          setError('Each level must allow at least one task kind');
          return prev;
        }
        setError('');
        return {
          ...prev,
          limitsByLevel: {
            ...prev.limitsByLevel,
            [levelKey]: {
              ...prev.limitsByLevel[levelKey],
              allowedTaskKinds: nextKinds,
            },
          },
        };
      });
    },
    [],
  );

  const handlePhotoRetentionLevelChange = useCallback(
    (levelKey: keyof PhotoRetentionDaysByLevel, value: number) => {
      setDraft((prev) => {
        if (!prev) {
          return prev;
        }
        const nextByLevel = {
          ...prev.photoRetentionDaysByLevel,
          [levelKey]: value,
        };
        return {
          ...prev,
          photoRetentionDaysByLevel: nextByLevel,
          // Keep flat default aligned with L1 for GCS fallback path.
          photoRetentionDays: levelKey === '1' ? value : prev.photoRetentionDays,
        };
      });
    },
    [],
  );

  const handlePinLimitFieldChange = useCallback(
    (
      levelKey: keyof PinMessageLimitsByLevel,
      fieldName: keyof PinMessageLimits,
      value: number,
    ) => {
      setDraft((prev) => {
        if (!prev) {
          return prev;
        }
        const nextByLevel = {
          ...prev.pinMessageLimitsByLevel,
          [levelKey]: {
            ...prev.pinMessageLimitsByLevel[levelKey],
            [fieldName]: value,
          },
        };
        const l1 = nextByLevel['1'];
        return {
          ...prev,
          pinMessageLimitsByLevel: nextByLevel,
          pinMessageMaxItems: l1.maxItems,
          pinMessageTitleMaxLength: l1.titleMaxLength,
          pinMessageContentMaxLength: l1.contentMaxLength,
        };
      });
    },
    [],
  );

  if (!draft) {
    return (
      <p className="text-sm text-slate-500" role="status">
        {t('common.loading')}
      </p>
    );
  }

  const field = (
    label: string,
    key: keyof SystemSettings,
    min: number,
    max: number,
  ) => (
    <div>
      <label className="mb-1 block text-xs text-slate-400" htmlFor={`set-${String(key)}`}>
        {label}
      </label>
      <input
        id={`set-${String(key)}`}
        type="number"
        min={min}
        max={max}
        value={draft[key] as number}
        onChange={(ev) =>
          setDraft((prev) => (prev ? { ...prev, [key]: num(ev.target.value) } : prev))
        }
        className={inputClass}
      />
    </div>
  );

  return (
    <div>
      <h1 className="text-2xl font-semibold text-white">{t('admin.nav.settings')}</h1>
      <p className="mt-1 text-sm text-slate-400">{t('admin.settingsSubtitle')}</p>

      <div className="mt-6 flex flex-wrap gap-2" role="tablist" aria-label={t('admin.nav.settings')}>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'membership'}
          className={tabClass('membership')}
          onClick={() => setTab('membership')}
        >
          {t('admin.settingsTab.membership')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'register'}
          className={tabClass('register')}
          onClick={() => setTab('register')}
        >
          {t('admin.settingsTab.register')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'subscription'}
          className={tabClass('subscription')}
          onClick={() => setTab('subscription')}
        >
          {t('admin.settingsTab.subscription')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'marketing'}
          className={tabClass('marketing')}
          onClick={() => setTab('marketing')}
        >
          {t('admin.settingsTab.marketing')}
        </button>
        {isSuperAdmin ? (
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'maintenance'}
            className={tabClass('maintenance')}
            onClick={() => setTab('maintenance')}
          >
            {t('admin.settingsTab.maintenance')}
          </button>
        ) : null}
      </div>

      {error ? (
        <p className="mt-4 text-sm text-red-400" role="alert">
          {error}
        </p>
      ) : null}

      {tab === 'membership' ? (
      <section
        className="mt-8 max-w-4xl rounded-lg border border-slate-800 bg-slate-900/60 p-6"
        aria-labelledby="limits-by-level-heading"
      >
        <h2 id="limits-by-level-heading" className="text-lg font-semibold text-white">
          Membership limits by level
        </h2>
        <p className="mt-1 text-sm text-slate-400">
          Edits <code className="text-amber-300">system_settings.limits_by_level</code>, photo/receipt
          retention, and pin message limits per level. Leave nullable caps as Unlimited for no limit
          (e.g. L3 total tasks).
        </p>

        <div className="mt-6 space-y-8">
          {LEVEL_KEYS.map((levelKey) => {
            const cfg = draft.limitsByLevel[levelKey];
            const pinCfg = draft.pinMessageLimitsByLevel[levelKey];
            return (
              <div
                key={levelKey}
                className="rounded-md border border-slate-700 bg-slate-950/50 p-4"
              >
                <h3 className="text-sm font-semibold text-amber-200">{LEVEL_LABELS[levelKey]}</h3>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <NullableNumberField
                    id={`lvl-${levelKey}-maxTotalTasks`}
                    label="Max total tasks"
                    value={cfg.maxTotalTasks}
                    onChange={(value) => handleLevelFieldChange(levelKey, 'maxTotalTasks', value)}
                  />
                  <div>
                    <label
                      className="mb-1 block text-xs text-slate-400"
                      htmlFor={`lvl-${levelKey}-maxDailyTaskSubmits`}
                    >
                      Max daily task submits (Helper)
                    </label>
                    <input
                      id={`lvl-${levelKey}-maxDailyTaskSubmits`}
                      type="number"
                      min={0}
                      value={cfg.maxDailyTaskSubmits}
                      onChange={(ev) =>
                        handleLevelFieldChange(levelKey, 'maxDailyTaskSubmits', num(ev.target.value))
                      }
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label
                      className="mb-1 block text-xs text-slate-400"
                      htmlFor={`lvl-${levelKey}-maxDailyTaskReviews`}
                    >
                      Max daily task reviews (Employer)
                    </label>
                    <input
                      id={`lvl-${levelKey}-maxDailyTaskReviews`}
                      type="number"
                      min={0}
                      value={cfg.maxDailyTaskReviews}
                      onChange={(ev) =>
                        handleLevelFieldChange(levelKey, 'maxDailyTaskReviews', num(ev.target.value))
                      }
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label
                      className="mb-1 block text-xs text-slate-400"
                      htmlFor={`lvl-${levelKey}-maxDailyReceipts`}
                    >
                      Max daily receipts
                    </label>
                    <input
                      id={`lvl-${levelKey}-maxDailyReceipts`}
                      type="number"
                      min={0}
                      value={cfg.maxDailyReceipts}
                      onChange={(ev) =>
                        handleLevelFieldChange(levelKey, 'maxDailyReceipts', num(ev.target.value))
                      }
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label
                      className="mb-1 block text-xs text-slate-400"
                      htmlFor={`lvl-${levelKey}-maxDailyAiAnalyze`}
                    >
                      Max daily AI analyze
                    </label>
                    <input
                      id={`lvl-${levelKey}-maxDailyAiAnalyze`}
                      type="number"
                      min={0}
                      value={cfg.maxDailyAiAnalyze}
                      onChange={(ev) =>
                        handleLevelFieldChange(levelKey, 'maxDailyAiAnalyze', num(ev.target.value))
                      }
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label
                      className="mb-1 block text-xs text-slate-400"
                      htmlFor={`lvl-${levelKey}-maxCommentsPerTarget`}
                    >
                      Max comments per target
                    </label>
                    <input
                      id={`lvl-${levelKey}-maxCommentsPerTarget`}
                      type="number"
                      min={1}
                      value={cfg.maxCommentsPerTarget}
                      onChange={(ev) =>
                        handleLevelFieldChange(
                          levelKey,
                          'maxCommentsPerTarget',
                          num(ev.target.value),
                        )
                      }
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label
                      className="mb-1 block text-xs text-slate-400"
                      htmlFor={`lvl-${levelKey}-maxCommentLength`}
                    >
                      Max comment length
                    </label>
                    <input
                      id={`lvl-${levelKey}-maxCommentLength`}
                      type="number"
                      min={1}
                      value={cfg.maxCommentLength}
                      onChange={(ev) =>
                        handleLevelFieldChange(levelKey, 'maxCommentLength', num(ev.target.value))
                      }
                      className={inputClass}
                    />
                  </div>
                  <NullableNumberField
                    id={`lvl-${levelKey}-maxInventoryLocations`}
                    label="Max inventory locations"
                    value={cfg.maxInventoryLocations}
                    onChange={(value) =>
                      handleLevelFieldChange(levelKey, 'maxInventoryLocations', value)
                    }
                    unlimitedLabel="N/A / unlimited (null)"
                  />
                  <NullableNumberField
                    id={`lvl-${levelKey}-maxInventoryCategories`}
                    label="Max inventory categories"
                    value={cfg.maxInventoryCategories}
                    onChange={(value) =>
                      handleLevelFieldChange(levelKey, 'maxInventoryCategories', value)
                    }
                    unlimitedLabel="N/A / unlimited (null)"
                  />
                  <div>
                    <label
                      className="mb-1 block text-xs text-slate-400"
                      htmlFor={`photo-retention-${levelKey}`}
                    >
                      Photo / receipt retention (days)
                    </label>
                    <input
                      id={`photo-retention-${levelKey}`}
                      type="number"
                      min={1}
                      max={365}
                      value={draft.photoRetentionDaysByLevel[levelKey]}
                      onChange={(ev) =>
                        handlePhotoRetentionLevelChange(levelKey, num(ev.target.value))
                      }
                      className={inputClass}
                      aria-label={`Photo retention days for ${LEVEL_LABELS[levelKey]}`}
                    />
                    <p className="mt-1 text-xs text-slate-500">
                      <code className="text-amber-300">storage_retention_days.levels</code>
                    </p>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-slate-200 sm:col-span-2">
                    <input
                      type="checkbox"
                      checked={cfg.autoTranslationEnabled}
                      onChange={(ev) =>
                        handleLevelFieldChange(
                          levelKey,
                          'autoTranslationEnabled',
                          ev.target.checked,
                        )
                      }
                    />
                    Auto translation enabled
                  </label>
                  <fieldset className="sm:col-span-2">
                    <legend className="mb-2 text-xs text-slate-400">Allowed task kinds</legend>
                    <div className="flex flex-wrap gap-3">
                      {TASK_KIND_OPTIONS.map((option) => (
                        <label
                          key={option.value}
                          className="flex items-center gap-2 text-sm text-slate-200"
                        >
                          <input
                            type="checkbox"
                            checked={cfg.allowedTaskKinds.includes(option.value)}
                            onChange={(ev) =>
                              handleToggleTaskKind(levelKey, option.value, ev.target.checked)
                            }
                          />
                          {option.label}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                </div>

                <div className="mt-5 rounded-md border border-slate-700 bg-slate-900/40 p-3">
                  <h4 className="text-xs font-semibold text-slate-200">Pin message limits</h4>
                  <p className="mt-1 text-xs text-slate-500">
                    <code className="text-amber-300">pin_message_limits_by_level.levels</code>
                  </p>
                  <div className="mt-3 grid gap-3 sm:grid-cols-3">
                    <div>
                      <label
                        className="mb-1 block text-xs text-slate-400"
                        htmlFor={`pin-maxItems-${levelKey}`}
                      >
                        Max items
                      </label>
                      <input
                        id={`pin-maxItems-${levelKey}`}
                        type="number"
                        min={1}
                        max={200}
                        value={pinCfg.maxItems}
                        onChange={(ev) =>
                          handlePinLimitFieldChange(levelKey, 'maxItems', num(ev.target.value))
                        }
                        className={inputClass}
                        aria-label={`Pin max items for ${LEVEL_LABELS[levelKey]}`}
                      />
                    </div>
                    <div>
                      <label
                        className="mb-1 block text-xs text-slate-400"
                        htmlFor={`pin-titleMax-${levelKey}`}
                      >
                        Title max length
                      </label>
                      <input
                        id={`pin-titleMax-${levelKey}`}
                        type="number"
                        min={1}
                        max={500}
                        value={pinCfg.titleMaxLength}
                        onChange={(ev) =>
                          handlePinLimitFieldChange(
                            levelKey,
                            'titleMaxLength',
                            num(ev.target.value),
                          )
                        }
                        className={inputClass}
                        aria-label={`Pin title max length for ${LEVEL_LABELS[levelKey]}`}
                      />
                    </div>
                    <div>
                      <label
                        className="mb-1 block text-xs text-slate-400"
                        htmlFor={`pin-contentMax-${levelKey}`}
                      >
                        Content max length
                      </label>
                      <input
                        id={`pin-contentMax-${levelKey}`}
                        type="number"
                        min={1}
                        max={5000}
                        value={pinCfg.contentMaxLength}
                        onChange={(ev) =>
                          handlePinLimitFieldChange(
                            levelKey,
                            'contentMaxLength',
                            num(ev.target.value),
                          )
                        }
                        className={inputClass}
                        aria-label={`Pin content max length for ${LEVEL_LABELS[levelKey]}`}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
      ) : null}

      {tab === 'register' ? (
      <>
      <section
        className="mt-8 max-w-2xl rounded-lg border border-slate-800 bg-slate-900/60 p-6"
        aria-labelledby="trial-window-heading"
      >
        <h2 id="trial-window-heading" className="text-lg font-semibold text-white">
          Trial window
        </h2>
        <p className="mt-1 text-sm text-slate-400">
          Controls first-pairing trial length and migration cutoff (
          <code className="text-amber-300">feature_launch_at</code> is entered as HKT; stored as
          UTC).
        </p>
        <div className="mt-4 grid gap-4">
          {field('Trial days (new employers)', 'trialDaysNew', 1, 365)}
          {field('Trial days (migration / pre-launch accounts)', 'trialDaysMigration', 1, 365)}
          <div>
            <label className="mb-1 block text-xs text-slate-400" htmlFor="feature-launch-at">
              Feature launch at (HKT, YYYY-MM-DD hh:mm)
            </label>
            <input
              id="feature-launch-at"
              type="text"
              value={draft.featureLaunchAt}
              placeholder="2026-07-01 08:00"
              onChange={(ev) =>
                setDraft((prev) =>
                  prev ? { ...prev, featureLaunchAt: ev.target.value } : prev,
                )
              }
              className={inputClass}
              aria-label="Feature launch at HKT"
            />
          </div>
        </div>
      </section>

      <div className="mt-8 grid max-w-2xl gap-6 rounded-lg border border-slate-800 bg-slate-900/60 p-6">
        <h2 className="text-lg font-semibold text-white">Register &amp; other</h2>
        <div>
          <label className="mb-1 block text-xs text-slate-400" htmlFor="consent-ver">
            {t('admin.set.consentVersion')}
          </label>
          <input
            id="consent-ver"
            type="text"
            value={draft.currentConsentVersion}
            onChange={(ev) =>
              setDraft((prev) =>
                prev ? { ...prev, currentConsentVersion: ev.target.value } : prev,
              )
            }
            className={inputClass}
          />
        </div>
        {field(t('admin.set.auditYears'), 'auditLogRetentionYears', 1, 10)}
        <div>
          <label className="flex items-center gap-2 text-sm text-slate-200">
            <input
              type="checkbox"
              checked={draft.invitationCodeRequired}
              onChange={(ev) =>
                setDraft((prev) =>
                  prev ? { ...prev, invitationCodeRequired: ev.target.checked } : prev,
                )
              }
            />
            {t('admin.set.invitationCodeRequired')}
          </label>
          <p className="mt-1 text-xs text-slate-500">{t('admin.set.invitationCodeRequiredHint')}</p>
        </div>
      </div>
      </>
      ) : null}

      {tab === 'subscription' ? (
      <>
      <section
        className="mt-8 max-w-3xl rounded-lg border border-slate-800 bg-slate-900/60 p-6"
        aria-labelledby="play-store-billing-notice-heading"
      >
        <h2 id="play-store-billing-notice-heading" className="text-lg font-semibold text-white">
          Google Play billing notice
        </h2>
        <label className="mt-4 flex items-start gap-2 text-sm text-slate-200">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={draft.playStoreShowOfficialWebsiteUrl}
            onChange={(ev) =>
              setDraft((prev) =>
                prev ? { ...prev, playStoreShowOfficialWebsiteUrl: ev.target.checked } : prev,
              )
            }
            aria-label={t('admin.set.playStoreShowOfficialWebsiteUrl')}
          />
          <span>
            {t('admin.set.playStoreShowOfficialWebsiteUrl')}
            <span className="mt-1 block text-xs text-slate-500">
              {t('admin.set.playStoreShowOfficialWebsiteUrlHint')}
            </span>
          </span>
        </label>
      </section>
      <section
        className="mt-8 max-w-3xl rounded-lg border border-slate-800 bg-slate-900/60 p-6"
        aria-labelledby="subscription-promotions-heading"
      >
        <h2 id="subscription-promotions-heading" className="text-lg font-semibold text-white">
          Subscription promotions
        </h2>
        <p className="mt-1 text-sm text-slate-400">
          Create matching Coupons in Stripe first (duration <code className="text-amber-300">once</code>
          ). Amount below uses major units (e.g. 20 = HKD 20); Stripe Coupon{' '}
          <code className="text-amber-300">amount_off</code> must use minor units (HKD 20 → 2000).
          Do not use Promotion Codes. When multiple offers apply on one charge, only the larger
          discount is used. New-user first month applies only to the first paid{' '}
          <strong className="text-slate-200">monthly</strong> invoice (not yearly). Limited-time
          offers (below) can target month or year and apply to every paid invoice in each offer&apos;s
          HKT window (including renewals via <code className="text-amber-300">invoice.created</code>).
        </p>

        <div className="mt-6 space-y-6">
          <div className="rounded-md border border-slate-700 bg-slate-950/50 p-4">
            <label className="flex items-center gap-2 text-sm text-slate-100">
              <input
                type="checkbox"
                checked={draft.subscriptionPromotions.newUserFirstMonth.enabled}
                onChange={(ev) =>
                  setDraft((prev) =>
                    prev
                      ? {
                          ...prev,
                          subscriptionPromotions: {
                            ...prev.subscriptionPromotions,
                            newUserFirstMonth: {
                              ...prev.subscriptionPromotions.newUserFirstMonth,
                              enabled: ev.target.checked,
                            },
                          },
                        }
                      : prev,
                  )
                }
              />
              New-user first paid month (monthly only)
            </label>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div>
                <label
                  className="mb-1 block text-xs text-slate-400"
                  htmlFor="promo-new-amount"
                >
                  Amount off (major units, e.g. 5 = HKD 5)
                </label>
                <input
                  id="promo-new-amount"
                  type="number"
                  min={0}
                  value={draft.subscriptionPromotions.newUserFirstMonth.amountOff}
                  onChange={(ev) =>
                    setDraft((prev) =>
                      prev
                        ? {
                            ...prev,
                            subscriptionPromotions: {
                              ...prev.subscriptionPromotions,
                              newUserFirstMonth: {
                                ...prev.subscriptionPromotions.newUserFirstMonth,
                                amountOff: num(ev.target.value),
                              },
                            },
                          }
                        : prev,
                    )
                  }
                  className={inputClass}
                />
              </div>
              <div>
                <label
                  className="mb-1 block text-xs text-slate-400"
                  htmlFor="promo-new-coupon"
                >
                  Stripe Coupon ID
                </label>
                <input
                  id="promo-new-coupon"
                  type="text"
                  value={draft.subscriptionPromotions.newUserFirstMonth.stripeCouponId}
                  onChange={(ev) =>
                    setDraft((prev) =>
                      prev
                        ? {
                            ...prev,
                            subscriptionPromotions: {
                              ...prev.subscriptionPromotions,
                              newUserFirstMonth: {
                                ...prev.subscriptionPromotions.newUserFirstMonth,
                                stripeCouponId: ev.target.value,
                              },
                            },
                          }
                        : prev,
                    )
                  }
                  className={inputClass}
                  placeholder="coupon_..."
                  aria-label="New-user Stripe Coupon ID"
                />
              </div>
            </div>
          </div>

          <div className="rounded-md border border-slate-700 bg-slate-950/50 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 className="text-sm font-medium text-slate-100">
                  Limited-time offers (multi)
                </h3>
                <p className="mt-1 text-xs text-slate-400">
                  Multiple offers with different date windows and billing intervals. On each charge,
                  only the highest amountOff offer that is in-window is applied. Yearly has no
                  new-user first-invoice discount.
                </p>
              </div>
              <button
                type="button"
                className="rounded-md border border-slate-600 px-3 py-1.5 text-xs text-slate-100 hover:bg-slate-800"
                aria-label="Add limited-time offer"
                onClick={() =>
                  setDraft((prev) =>
                    prev
                      ? {
                          ...prev,
                          subscriptionPromotions: {
                            ...prev.subscriptionPromotions,
                            limitedTimeOffers: [
                              ...prev.subscriptionPromotions.limitedTimeOffers,
                              createEmptyLimitedTimeOffer(),
                            ],
                          },
                        }
                      : prev,
                  )
                }
              >
                Add offer
              </button>
            </div>
            <div className="mt-4 space-y-4">
              {draft.subscriptionPromotions.limitedTimeOffers.length === 0 ? (
                <p className="text-xs text-slate-500">No limited-time offers configured.</p>
              ) : null}
              {draft.subscriptionPromotions.limitedTimeOffers.map((offer, index) => (
                <div
                  key={offer.id}
                  className="rounded-md border border-slate-700 bg-slate-900/60 p-3"
                >
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <label className="flex items-center gap-2 text-sm text-slate-100">
                      <input
                        type="checkbox"
                        checked={offer.enabled}
                        onChange={(ev) =>
                          setDraft((prev) => {
                            if (!prev) {
                              return prev;
                            }
                            const nextOffers = prev.subscriptionPromotions.limitedTimeOffers.map(
                              (row, i) =>
                                i === index ? { ...row, enabled: ev.target.checked } : row,
                            );
                            return {
                              ...prev,
                              subscriptionPromotions: {
                                ...prev.subscriptionPromotions,
                                limitedTimeOffers: nextOffers,
                              },
                            };
                          })
                        }
                      />
                      Enabled — offer {index + 1}
                    </label>
                    <button
                      type="button"
                      className="text-xs text-red-300 hover:text-red-200"
                      aria-label={`Remove limited-time offer ${index + 1}`}
                      onClick={() =>
                        setDraft((prev) => {
                          if (!prev) {
                            return prev;
                          }
                          return {
                            ...prev,
                            subscriptionPromotions: {
                              ...prev.subscriptionPromotions,
                              limitedTimeOffers:
                                prev.subscriptionPromotions.limitedTimeOffers.filter(
                                  (_, i) => i !== index,
                                ),
                            },
                          };
                        })
                      }
                    >
                      Remove
                    </button>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label
                        className="mb-1 block text-xs text-slate-400"
                        htmlFor={`promo-offer-interval-${offer.id}`}
                      >
                        Billing interval
                      </label>
                      <select
                        id={`promo-offer-interval-${offer.id}`}
                        value={offer.billingInterval}
                        onChange={(ev) =>
                          setDraft((prev) => {
                            if (!prev) {
                              return prev;
                            }
                            const billingInterval: LimitedTimePromoOffer['billingInterval'] =
                              ev.target.value === 'year' ? 'year' : 'month';
                            const nextOffers = prev.subscriptionPromotions.limitedTimeOffers.map(
                              (row, i) => (i === index ? { ...row, billingInterval } : row),
                            );
                            return {
                              ...prev,
                              subscriptionPromotions: {
                                ...prev.subscriptionPromotions,
                                limitedTimeOffers: nextOffers,
                              },
                            };
                          })
                        }
                        className={inputClass}
                        aria-label={`Billing interval for offer ${index + 1}`}
                      >
                        <option value="month">month</option>
                        <option value="year">year</option>
                      </select>
                    </div>
                    <div>
                      <label
                        className="mb-1 block text-xs text-slate-400"
                        htmlFor={`promo-offer-amount-${offer.id}`}
                      >
                        Amount off (major units)
                      </label>
                      <input
                        id={`promo-offer-amount-${offer.id}`}
                        type="number"
                        min={0}
                        value={offer.amountOff}
                        onChange={(ev) =>
                          setDraft((prev) => {
                            if (!prev) {
                              return prev;
                            }
                            const nextOffers = prev.subscriptionPromotions.limitedTimeOffers.map(
                              (row, i) =>
                                i === index ? { ...row, amountOff: num(ev.target.value) } : row,
                            );
                            return {
                              ...prev,
                              subscriptionPromotions: {
                                ...prev.subscriptionPromotions,
                                limitedTimeOffers: nextOffers,
                              },
                            };
                          })
                        }
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label
                        className="mb-1 block text-xs text-slate-400"
                        htmlFor={`promo-offer-coupon-${offer.id}`}
                      >
                        Stripe Coupon ID
                      </label>
                      <input
                        id={`promo-offer-coupon-${offer.id}`}
                        type="text"
                        value={offer.stripeCouponId}
                        onChange={(ev) =>
                          setDraft((prev) => {
                            if (!prev) {
                              return prev;
                            }
                            const nextOffers = prev.subscriptionPromotions.limitedTimeOffers.map(
                              (row, i) =>
                                i === index ? { ...row, stripeCouponId: ev.target.value } : row,
                            );
                            return {
                              ...prev,
                              subscriptionPromotions: {
                                ...prev.subscriptionPromotions,
                                limitedTimeOffers: nextOffers,
                              },
                            };
                          })
                        }
                        className={inputClass}
                        placeholder="coupon_..."
                        aria-label={`Stripe Coupon ID for offer ${index + 1}`}
                      />
                    </div>
                    <div>
                      <label
                        className="mb-1 block text-xs text-slate-400"
                        htmlFor={`promo-offer-note-${offer.id}`}
                      >
                        Admin note (optional)
                      </label>
                      <input
                        id={`promo-offer-note-${offer.id}`}
                        type="text"
                        value={offer.adminNote ?? ''}
                        onChange={(ev) =>
                          setDraft((prev) => {
                            if (!prev) {
                              return prev;
                            }
                            const nextOffers = prev.subscriptionPromotions.limitedTimeOffers.map(
                              (row, i) =>
                                i === index ? { ...row, adminNote: ev.target.value } : row,
                            );
                            return {
                              ...prev,
                              subscriptionPromotions: {
                                ...prev.subscriptionPromotions,
                                limitedTimeOffers: nextOffers,
                              },
                            };
                          })
                        }
                        className={inputClass}
                        maxLength={200}
                      />
                    </div>
                    <div>
                      <label
                        className="mb-1 block text-xs text-slate-400"
                        htmlFor={`promo-offer-start-${offer.id}`}
                      >
                        Starts at (HKT, YYYY-MM-DD hh:mm)
                      </label>
                      <input
                        id={`promo-offer-start-${offer.id}`}
                        type="text"
                        value={offer.startsAt}
                        onChange={(ev) =>
                          setDraft((prev) => {
                            if (!prev) {
                              return prev;
                            }
                            const nextOffers = prev.subscriptionPromotions.limitedTimeOffers.map(
                              (row, i) =>
                                i === index ? { ...row, startsAt: ev.target.value } : row,
                            );
                            return {
                              ...prev,
                              subscriptionPromotions: {
                                ...prev.subscriptionPromotions,
                                limitedTimeOffers: nextOffers,
                              },
                            };
                          })
                        }
                        className={inputClass}
                        placeholder="2026-01-01 00:00"
                        aria-label={`Promo offer starts at HKT ${offer.id}`}
                      />
                    </div>
                    <div>
                      <label
                        className="mb-1 block text-xs text-slate-400"
                        htmlFor={`promo-offer-end-${offer.id}`}
                      >
                        Ends at (HKT, YYYY-MM-DD hh:mm)
                      </label>
                      <input
                        id={`promo-offer-end-${offer.id}`}
                        type="text"
                        value={offer.endsAt}
                        onChange={(ev) =>
                          setDraft((prev) => {
                            if (!prev) {
                              return prev;
                            }
                            const nextOffers = prev.subscriptionPromotions.limitedTimeOffers.map(
                              (row, i) => (i === index ? { ...row, endsAt: ev.target.value } : row),
                            );
                            return {
                              ...prev,
                              subscriptionPromotions: {
                                ...prev.subscriptionPromotions,
                                limitedTimeOffers: nextOffers,
                              },
                            };
                          })
                        }
                        className={inputClass}
                        placeholder="2026-03-31 23:59"
                        aria-label={`Promo offer ends at HKT ${offer.id}`}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section
        className="mt-8 max-w-3xl rounded-lg border border-slate-800 bg-slate-900/60 p-6"
        aria-labelledby="subscription-upgrade-copy-heading"
      >
        <h2 id="subscription-upgrade-copy-heading" className="text-lg font-semibold text-white">
          Subscription upgrade copy
        </h2>
        <p className="mt-1 text-sm text-slate-400">
          Editable copy on the employer subscription upgrade card. Locale codes are open-ended.
          Three groups per locale: <strong className="text-slate-200">normal</strong> (charge today,
          no promo), <strong className="text-slate-200">promo pricing</strong> (discounted charge
          today), and <strong className="text-slate-200">deferred first payment</strong> (invitation
          premium trial / no charge today). Placeholders are replaced with formatted amounts and
          dates at runtime.
        </p>

        <div className="mt-4">
          <label className="mb-1 block text-xs text-slate-400" htmlFor="upgrade-default-locale">
            Default locale (fallback)
          </label>
          <select
            id="upgrade-default-locale"
            value={draft.subscriptionUpgradeCopy.defaultLocale}
            onChange={(ev) =>
              setDraft((prev) =>
                prev
                  ? {
                      ...prev,
                      subscriptionUpgradeCopy: {
                        ...prev.subscriptionUpgradeCopy,
                        defaultLocale: ev.target.value,
                      },
                    }
                  : prev,
              )
            }
            className={inputClass}
          >
            {localeCodes.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-4 flex flex-wrap items-end gap-2">
          <div>
            <label className="mb-1 block text-xs text-slate-400" htmlFor="upgrade-new-locale">
              Add locale code
            </label>
            <input
              id="upgrade-new-locale"
              type="text"
              value={newLocaleCode}
              placeholder="e.g. ja, ko, zh-Hant"
              onChange={(ev) => setNewLocaleCode(ev.target.value)}
              onKeyDown={(ev) => {
                if (ev.key === 'Enter') {
                  ev.preventDefault();
                  handleAddLocale();
                }
              }}
              className="w-48 rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
              aria-label="New locale code"
            />
          </div>
          <button
            type="button"
            onClick={handleAddLocale}
            className="rounded-md border border-slate-600 px-3 py-2 text-sm text-slate-100 hover:bg-slate-800"
          >
            Add locale
          </button>
        </div>

        <div className="mt-6 space-y-6">
          {localeCodes.map((code) => {
            const row = draft.subscriptionUpgradeCopy.locales[code];
            if (!row) {
              return null;
            }
            return (
              <div
                key={code}
                className="rounded-md border border-slate-700 bg-slate-950/60 p-4"
              >
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-mono text-sm font-semibold text-amber-200">{code}</h3>
                  <button
                    type="button"
                    onClick={() => handleRemoveLocale(code)}
                    className="text-xs text-red-300 hover:text-red-200"
                    aria-label={`Remove locale ${code}`}
                  >
                    Remove
                  </button>
                </div>
                <div className="mt-4 space-y-6">
                  {UPGRADE_COPY_FIELD_GROUPS.map((group) => (
                    <div key={group.heading}>
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                        {group.heading}
                      </h4>
                      <div className="mt-3 space-y-3">
                        {group.fields.map((field) => {
                          const fieldId = `upgrade-${field.key}-${code}`;
                          const value = row[field.key];
                          return (
                            <div key={field.key}>
                              <label
                                className="mb-1 block text-xs text-slate-400"
                                htmlFor={fieldId}
                              >
                                {field.label}
                                {field.hint ? (
                                  <span className="ml-1 text-slate-500">
                                    (optional placeholders: {field.hint})
                                  </span>
                                ) : null}
                              </label>
                              {field.multiline ? (
                                <textarea
                                  id={fieldId}
                                  rows={2}
                                  value={value}
                                  onChange={(ev) =>
                                    setDraft((prev) => {
                                      if (!prev) {
                                        return prev;
                                      }
                                      return {
                                        ...prev,
                                        subscriptionUpgradeCopy: {
                                          ...prev.subscriptionUpgradeCopy,
                                          locales: {
                                            ...prev.subscriptionUpgradeCopy.locales,
                                            [code]: {
                                              ...prev.subscriptionUpgradeCopy.locales[code]!,
                                              [field.key]: ev.target.value,
                                            },
                                          },
                                        },
                                      };
                                    })
                                  }
                                  className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
                                />
                              ) : (
                                <input
                                  id={fieldId}
                                  type="text"
                                  value={value}
                                  onChange={(ev) =>
                                    setDraft((prev) => {
                                      if (!prev) {
                                        return prev;
                                      }
                                      return {
                                        ...prev,
                                        subscriptionUpgradeCopy: {
                                          ...prev.subscriptionUpgradeCopy,
                                          locales: {
                                            ...prev.subscriptionUpgradeCopy.locales,
                                            [code]: {
                                              ...prev.subscriptionUpgradeCopy.locales[code]!,
                                              [field.key]: ev.target.value,
                                            },
                                          },
                                        },
                                      };
                                    })
                                  }
                                  className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>
      </>
      ) : null}

      {tab === 'marketing' ? (
      <section
        className="mt-8 max-w-4xl rounded-lg border border-slate-800 bg-slate-900/60 p-6"
        aria-labelledby="membership-pricing-catalog-heading"
      >
        <h2 id="membership-pricing-catalog-heading" className="text-lg font-semibold text-white">
          Membership pricing table (marketing)
        </h2>
        <p className="mt-1 text-sm text-slate-400">
          Controls which limit rows appear on the public marketing pricing API / PHP site.
          Values always sync from live Admin limits, Stripe monthly price, and promotions.
          <strong className="text-slate-200"> Accounts</strong> and
          <strong className="text-slate-200"> Price</strong> are always shown first and cannot be
          disabled. Remarks (3 locales) show as hover tooltips when non-empty.
        </p>

        <div className="mt-6 space-y-4">
          {draft.membershipPricingCatalog.items.map((row, index) => {
            const locked = isMembershipPricingLockedKey(row.key);
            const unlockedIndex = draft.membershipPricingCatalog.items
              .filter((item) => !isMembershipPricingLockedKey(item.key))
              .findIndex((item) => item.key === row.key);
            const unlockedCount = draft.membershipPricingCatalog.items.filter(
              (item) => !isMembershipPricingLockedKey(item.key),
            ).length;
            return (
              <div
                key={row.key}
                className="rounded-md border border-slate-700 bg-slate-950/60 p-4"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="font-mono text-sm font-semibold text-amber-200">{row.key}</h3>
                    {locked ? (
                      <p className="mt-0.5 text-xs text-slate-500">Locked — always visible</p>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="flex items-center gap-2 text-xs text-slate-300">
                      <input
                        type="checkbox"
                        checked={row.enabled}
                        disabled={locked}
                        onChange={(ev) =>
                          setDraft((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  membershipPricingCatalog: updateCatalogItem(
                                    prev.membershipPricingCatalog,
                                    row.key,
                                    { enabled: ev.target.checked },
                                  ),
                                }
                              : prev,
                          )
                        }
                        aria-label={`Show ${row.key} on marketing API`}
                      />
                      Show on API
                    </label>
                    {!locked ? (
                      <div className="flex gap-1">
                        <button
                          type="button"
                          disabled={unlockedIndex <= 0}
                          className="rounded border border-slate-600 px-2 py-1 text-xs text-slate-200 hover:bg-slate-800 disabled:opacity-40"
                          onClick={() =>
                            setDraft((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    membershipPricingCatalog: moveCatalogItem(
                                      prev.membershipPricingCatalog,
                                      row.key,
                                      -1,
                                    ),
                                  }
                                : prev,
                            )
                          }
                          aria-label={`Move ${row.key} up`}
                        >
                          Up
                        </button>
                        <button
                          type="button"
                          disabled={unlockedIndex < 0 || unlockedIndex >= unlockedCount - 1}
                          className="rounded border border-slate-600 px-2 py-1 text-xs text-slate-200 hover:bg-slate-800 disabled:opacity-40"
                          onClick={() =>
                            setDraft((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    membershipPricingCatalog: moveCatalogItem(
                                      prev.membershipPricingCatalog,
                                      row.key,
                                      1,
                                    ),
                                  }
                                : prev,
                            )
                          }
                          aria-label={`Move ${row.key} down`}
                        >
                          Down
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                  {CATALOG_LOCALES.map((locale) => (
                    <div key={`${row.key}-label-${locale}`}>
                      <label
                        className="mb-1 block text-xs text-slate-400"
                        htmlFor={`catalog-label-${row.key}-${locale}`}
                      >
                        Label ({locale})
                      </label>
                      <input
                        id={`catalog-label-${row.key}-${locale}`}
                        type="text"
                        value={row.label[locale]}
                        onChange={(ev) =>
                          setDraft((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  membershipPricingCatalog: updateCatalogItem(
                                    prev.membershipPricingCatalog,
                                    row.key,
                                    {
                                      label: { ...row.label, [locale]: ev.target.value },
                                    },
                                  ),
                                }
                              : prev,
                          )
                        }
                        className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
                      />
                    </div>
                  ))}
                </div>

                <div className="mt-3 grid gap-3 sm:grid-cols-3">
                  {CATALOG_LOCALES.map((locale) => (
                    <div key={`${row.key}-remark-${locale}`}>
                      <label
                        className="mb-1 block text-xs text-slate-400"
                        htmlFor={`catalog-remark-${row.key}-${locale}`}
                      >
                        Remark / tooltip ({locale})
                      </label>
                      <textarea
                        id={`catalog-remark-${row.key}-${locale}`}
                        rows={2}
                        value={row.remark[locale]}
                        onChange={(ev) =>
                          setDraft((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  membershipPricingCatalog: updateCatalogItem(
                                    prev.membershipPricingCatalog,
                                    row.key,
                                    {
                                      remark: { ...row.remark, [locale]: ev.target.value },
                                    },
                                  ),
                                }
                              : prev,
                          )
                        }
                        className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
                      />
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-xs text-slate-500">Row order index: {index}</p>
              </div>
            );
          })}
        </div>
      </section>
      ) : null}

      {tab === 'maintenance' && isSuperAdmin ? (
        <section
          className="mt-8 max-w-2xl rounded-lg border border-slate-800 bg-slate-900/60 p-6"
          aria-labelledby="maintenance-settings-heading"
        >
          <h2 id="maintenance-settings-heading" className="text-lg font-semibold text-white">
            {t('admin.settingsTab.maintenance')}
          </h2>
          <p className="mt-1 text-sm text-slate-400">{t('admin.maintenance.subtitle')}</p>
          <div className="mt-6">
            <AdminMaintenanceForm variant="settings" />
          </div>
        </section>
      ) : null}

      {tab !== 'maintenance' ? (
        <button
          type="button"
          disabled={saving || JSON.stringify(settings) === JSON.stringify(draft)}
          className="mt-8 rounded-md bg-amber-500 px-6 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-50"
          onClick={() => void handleSave()}
        >
          {saving ? t('admin.saving') : t('admin.saveSettings')}
        </button>
      ) : null}
    </div>
  );
};
